/**
 * questionnaireAnswers.js — rendering a submitted questionnaire's answers, read-only
 * or as an editable form.
 *
 * Extracted from clients-route.js's send/cancel modal so the reseller review page could
 * show the same thing without a second implementation. The EDIT half now lives here too
 * (editFieldHtml + collectAnswers), because it stopped being modal-specific the moment a
 * second and third surface needed it: the admin console's Questionnaires drawer, and the
 * client's own post-submission edit on the public fill page.
 *
 * Four surfaces, one renderer and one collector:
 *   client-saas /clients modal   — staff edit, in-window
 *   client-saas /questionnaires  — read-only review
 *   client-sse-console           — admin read + edit
 *   client-saas fill page        — the client's own edit, unauthenticated
 * Getting the FIELDS and the COLLECTION from the same place is the part that matters:
 * they are two halves of one contract (`data-question-id` / `data-question-type` on a
 * wrapper, inputs inside it), and a surface that renders with one and reads with its own
 * hand-rolled copy of the other silently drops answers — checkbox groups especially.
 *
 * The .qm-* styles moved with it. Two copies of a stylesheet is the same drift problem
 * as two copies of the markup, just quieter — the second copy is the one that stops
 * getting updated.
 */

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

export function fmtDateTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}

/**
 * One answer, as display text. Returns '' when unanswered — callers decide how to show
 * that, because "blank" reads differently in a modal than in a review drawer.
 */
export function answerDisplay(q, answers = {}) {
  const val = answers[q.id];
  if (q.type === 'checkbox_group') {
    const selected = Array.isArray(val) ? val : [];
    return selected.length ? esc(selected.join(', ')) : '';
  }
  return val ? esc(val) : '';
}

/**
 * The question/answer body of a submitted questionnaire: sections as headings, each
 * question's label above its answer.
 *
 * Unanswered questions are RENDERED, not skipped. A reviewer needs to tell "they left
 * it blank" apart from "we never asked", and dropping the row makes those identical.
 *
 * @param {object} data a response record ({ questionnaire: { sections }, answers })
 * @param {object} [opts]
 * @param {string} [opts.emptyLabel] text for an unanswered question
 * @param {(q: object, answers: object) => string} [opts.renderAnswer] override the
 *   answer cell — the modal passes its edit-field renderer through here, which is how
 *   edit mode reuses this same section/question scaffolding instead of duplicating it.
 */
export function answersHtml(data, { emptyLabel = '(blank)', renderAnswer = null } = {}) {
  const sections = data?.questionnaire?.sections || [];
  const answers = data?.answers || {};
  if (!sections.length) return '<p class="qm-qa__a qm-qa__a--empty">This questionnaire has no questions.</p>';

  return sections.map((s) => `
    <div class="qm-section">
      <h3>${esc(s.title)}</h3>
      ${(s.questions || []).map((q) => `
        <div class="qm-qa" data-question-id="${esc(q.id)}" data-question-type="${esc(q.type)}">
          <div class="qm-qa__q">${esc(q.label)}</div>
          <div class="qm-qa__a">${renderAnswer
            ? renderAnswer(q, answers)
            : (answerDisplay(q, answers) || `<span class="qm-qa__a--empty">${esc(emptyLabel)}</span>`)}</div>
        </div>`).join('')}
    </div>`).join('');
}

/**
 * The EDIT counterpart of answerDisplay: one answer as an input control. Pass it to
 * answersHtml as `renderAnswer` and the same section/question scaffolding renders as a
 * form — no parallel copy of the layout that only edit mode exercises.
 *
 * The wrapper (with data-question-id/data-question-type) comes from answersHtml; this
 * renders only the control inside it, which is what keeps collectAnswers below able to
 * read anything this produces.
 */
export function editFieldHtml(q, answers = {}) {
  const val = answers[q.id];
  if (q.type === 'checkbox_group') {
    const selected = Array.isArray(val) ? val : [];
    const options = Array.isArray(q.options) ? q.options : [];
    return `<div class="qm-checkbox-group">${options.map((opt) => `
      <label><input type="checkbox" value="${esc(opt)}" ${selected.includes(opt) ? 'checked' : ''}/> ${esc(opt)}</label>`).join('')}</div>`;
  }
  if (q.type === 'textarea') return `<textarea>${esc(val || '')}</textarea>`;
  return `<input type="text" value="${esc(val || '')}" />`;
}

/**
 * Read an edit form back into an answers object — the other half of editFieldHtml.
 *
 * OMITS empty answers rather than storing "". A blank answer and an unanswered question
 * are the same thing to this product ("leave anything that does not apply blank"), and
 * the read-only renderer already distinguishes "blank" from "not asked" by rendering
 * every question regardless.
 *
 * @param {ParentNode} root any element containing the [data-question-id] wrappers
 */
export function collectAnswers(root) {
  const answers = {};
  if (!root) return answers;
  root.querySelectorAll('[data-question-id]').forEach((fieldEl) => {
    const qid = fieldEl.dataset.questionId;
    if (fieldEl.dataset.questionType === 'checkbox_group') {
      const checked = [...fieldEl.querySelectorAll('input[type="checkbox"]:checked')].map((cb) => cb.value);
      if (checked.length) answers[qid] = checked;
    } else {
      const value = (fieldEl.querySelector('input,textarea')?.value || '').trim();
      if (value) answers[qid] = value;
    }
  });
  return answers;
}

/**
 * Provenance lines shown above the answers: who it went to, who last touched it.
 *
 * Staff edits and CLIENT self-edits are reported separately and both are shown when
 * both happened. They are genuinely different events — "we corrected their answer"
 * versus "they corrected it themselves" — and collapsing them into one line was the
 * reason client_edited_tz exists as its own column rather than reusing last_edited_tz.
 */
export function answersMetaHtml(data) {
  const recipient = data?.recipient_email
    ? `<p class="qm-recipient">Sent to ${esc(data.recipient_email)}</p>` : '';
  const edited = data?.last_edited_tz
    ? `<p class="qm-edited-marker">Last edited by ${esc(data.last_edited_by_email || 'a team member')} on ${esc(fmtDateTime(data.last_edited_tz))}</p>`
    : '';
  const clientEdited = data?.client_edited_tz
    ? `<p class="qm-edited-marker">Updated by the client on ${esc(fmtDateTime(data.client_edited_tz))}</p>`
    : '';
  return recipient + edited + clientEdited;
}

let stylesInjected = false;

/** Inject the shared .qm-* styles once. Both surfaces call this. */
export function ensureQuestionnaireAnswerStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-questionnaire-answer-styles';
  style.textContent = `
    .qm-status { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: var(--radius-md, 10px); background: var(--color-surface, #F4F7FB); margin-bottom: 16px; font-size: 0.85rem; }
    .qm-status .material-symbols-outlined { color: var(--color-primary-600, #0072BA); }
    .qm-status[data-status="SUBMITTED"] .material-symbols-outlined { color: var(--color-success, #009473); }
    .qm-status[data-status="CANCELLED"] .material-symbols-outlined { color: var(--color-danger, #DD4124); }
    .qm-status[data-status="LOCKED"] { background: var(--color-surface-2, #EAF0F7); }
    .qm-status[data-status="LOCKED"] .material-symbols-outlined { color: var(--color-text-muted, #4A5A78); }
    .qm-recipient { font-size: 0.78rem; color: var(--color-text-subtle, #6C7C9A); margin: -8px 0 16px; }
    .qm-edited-marker { font-size: 0.76rem; color: var(--color-text-subtle, #6C7C9A); margin: -8px 0 16px; }
    .qm-section { margin-bottom: 18px; }
    .qm-section h3 { font-size: 0.92rem; font-weight: 600; margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid var(--color-border, #D4DCE8); }
    .qm-qa { margin-bottom: 10px; }
    .qm-qa__q { font-size: 0.78rem; font-weight: 600; color: var(--color-text-subtle, #6C7C9A); margin-bottom: 3px; }
    .qm-qa__a { font-size: 0.9rem; white-space: pre-wrap; }
    .qm-qa__a.qm-qa__a--empty, .qm-qa__a .qm-qa__a--empty { color: var(--color-text-subtle, #6C7C9A); font-style: italic; }
    .qm-qa__a input[type="text"], .qm-qa__a textarea {
      width: 100%; box-sizing: border-box; font: inherit; font-size: 0.88rem; padding: 8px 10px;
      border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 8px); background: var(--color-bg, #fff); color: var(--color-text, #0B1220);
    }
    .qm-qa__a textarea { min-height: 60px; resize: vertical; }
    .qm-qa__a .qm-checkbox-group { display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 0.85rem; }
    .qm-qa__a .qm-checkbox-group label { display: flex; align-items: center; gap: 5px; }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

export default {
  answersHtml,
  answersMetaHtml,
  answerDisplay,
  editFieldHtml,
  collectAnswers,
  fmtDateTime,
  ensureQuestionnaireAnswerStyles,
};
